import { createActions } from "direct-vuex";
import Axios, { AxiosResponse } from "axios";
import config from "@/config";

import {
  RequestIndexResponse,
  RequestShowResponse,
  GeneratePayload
} from "./types";
import { requestActionContext } from "./index";
import { InputTableType } from "../form/types";

const path = config.hostname + "/requests/";

export default createActions({
  async get(context) {
    const { commit, rootCommit, rootState } = requestActionContext(context);
    try {
      rootCommit.START_LOADING();
      let result: AxiosResponse<RequestIndexResponse> = await Axios.get(path, {
        params: { pagination: rootState.pagination, filters: rootState.filters }
      });
      // Parse API data to include relationships
      result.data.requests.included.forEach(incl => {
        result.data.requests.data.forEach(req => {
          if (
            incl.type == "user" &&
            req.relationships.user.data.id == incl.id
          ) {
            req.relationships.user.data = incl;
          } else if (
            incl.type == "batch" &&
            req.relationships.batch.data.id == incl.id
          ) {
            req.relationships.batch.data = incl;
          } else if (
            incl.type == "company" &&
            req.relationships.company.data.id == incl.id
          ) {
            req.relationships.company.data = incl;
          } else if (incl.type == "template") {
            req.relationships.templates.data.forEach((template, index) => {
              if (template.id == incl.id) {
                req.relationships.templates.data[index] = incl;
              }
            });
          } else if (incl.type == "form") {
            req.relationships.forms.data.forEach((form, index) => {
              if (form.id == incl.id) {
                req.relationships.forms.data[index] = incl;
              }
            });
          }
        });
      });
      commit.SET_REQUESTS(result.data.requests.data);
      rootCommit.SET_TOTAL_ITEMS(result.data.pagination.totalItems);
      rootCommit.END_LOADING();
      return { data: result.data.requests.data };
    } catch (error) {
      console.log(error);
      rootCommit.END_LOADING();
      return { error };
    }
  },
  async find(context, id: string) {
    const { commit, rootCommit } = requestActionContext(context);
    try {
      rootCommit.START_LOADING();
      let result: AxiosResponse<RequestShowResponse> = await Axios.get(
        path + id
      );
      // Parse API data to include relationships
      result.data.included.forEach(incl => {
        if (
          incl.type == "user" &&
          result.data.data.relationships.user.data.id == incl.id
        ) {
          result.data.data.relationships.user.data = incl;
        } else if (
          incl.type == "batch" &&
          result.data.data.relationships.batch.data.id == incl.id
        ) {
          result.data.data.relationships.batch.data = incl;
        } else if (
          incl.type == "company" &&
          result.data.data.relationships.company.data.id == incl.id
        ) {
          result.data.data.relationships.company.data = incl;
        } else if (incl.type == "template") {
          result.data.data.relationships.templates.data.forEach(
            (template, index) => {
              if (template.id == incl.id) {
                result.data.data.relationships.templates.data[index] = incl;
              }
            }
          );
        } else if (incl.type == "form") {
          result.data.data.relationships.forms.data.forEach((form, index) => {
            if (form.id == incl.id) {
              result.data.data.relationships.forms.data[index] = incl;
            }
          });
        }
      });
      commit.SET_SELECTED(result.data.data);
      rootCommit.END_LOADING();
      return { data: result.data.data };
    } catch (error) {
      console.log(error);
      rootCommit.END_LOADING();
      return { error };
    }
  },
  async generate(context, id: string) {
    const { state, commit, rootCommit, rootState } = requestActionContext(
      context
    );
    if (state.selected && state.selected.id == id) {
      try {
        rootCommit.START_LOADING();
        const request = state.selected;
        const master = rootState.form.selected;
        let payload: GeneratePayload = { request: { forms_attributes: [] } };
        request.relationships.templates.data.forEach(template => {
          let form = request.relationships.forms.data.find(f => {
            return f.relationships.template.data.id == template.id;
          });
          if (form) {
            payload.request.forms_attributes.push({ id: form.id, data: {} });
            template.attributes.sections.forEach(section => {
              section.inputs.forEach(input => {
                let idx = payload.request.forms_attributes.length - 1;
                if (input.type == "text" || input.type == "number") {
                  // If input type is text or number
                  payload.request.forms_attributes[idx].data[
                    input.name.toLowerCase()
                  ] = master[input.name.toLowerCase()] || "";
                } else if (input.type == "radio" || input.type == "checkbox") {
                  // If input type is radio or checkbox
                  if (master[input.name.toLowerCase()]) {
                    payload.request.forms_attributes[idx].data[
                      input.name.toLowerCase() + "_v"
                    ] = "X";
                    payload.request.forms_attributes[idx].data[
                      input.name.toLowerCase() + "_f"
                    ] = " ";
                  } else {
                    payload.request.forms_attributes[idx].data[
                      input.name.toLowerCase() + "_v"
                    ] = " ";
                    payload.request.forms_attributes[idx].data[
                      input.name.toLowerCase() + "_f"
                    ] = "X";
                  }
                } else {
                  // If input type is table
                  const table = master[input.name.toLowerCase()];
                  if (Array.isArray(table)) {
                    const table = master[
                      input.name.toLowerCase()
                    ] as InputTableType[];
                    [...Array(10).keys()].forEach(val => {
                      if (val < table.length) {
                        const row = table[val];
                        Object.keys(row).forEach(header => {
                          payload.request.forms_attributes[idx].data[
                            input.name.toLowerCase() +
                              "_" +
                              val.toString() +
                              "_" +
                              header.toLowerCase()
                          ] = row[header];
                        });
                      } else {
                        input.headers.forEach(header => {
                          payload.request.forms_attributes[idx].data[
                            input.name.toLowerCase() +
                              "_" +
                              val.toString() +
                              "_" +
                              header.name.toLowerCase()
                          ] = " ";
                        });
                      }
                    });
                  }
                }
              });
            });
          } else {
            throw Error("Corresponding form not found...");
          }
        });

        let result: AxiosResponse<RequestShowResponse> = await Axios.put(
          path + id,
          payload
        );
        // Parse API data to include relationships
        result.data.included.forEach(incl => {
          if (
            incl.type == "user" &&
            result.data.data.relationships.user.data.id == incl.id
          ) {
            result.data.data.relationships.user.data = incl;
          } else if (
            incl.type == "batch" &&
            result.data.data.relationships.batch.data.id == incl.id
          ) {
            result.data.data.relationships.batch.data = incl;
          } else if (
            incl.type == "company" &&
            result.data.data.relationships.company.data.id == incl.id
          ) {
            result.data.data.relationships.company.data = incl;
          } else if (incl.type == "template") {
            result.data.data.relationships.templates.data.forEach(
              (template, index) => {
                if (template.id == incl.id) {
                  result.data.data.relationships.templates.data[index] = incl;
                }
              }
            );
          } else if (incl.type == "form") {
            result.data.data.relationships.forms.data.forEach((form, index) => {
              if (form.id == incl.id) {
                result.data.data.relationships.forms.data[index] = incl;
              }
            });
          }
        });
        commit.SET_SELECTED(result.data.data);
        rootCommit.END_LOADING();
        return { data: result.data.data };
      } catch (error) {
        console.log(error);
        rootCommit.END_LOADING();
        return { error };
      }
    }
  },
  async sign(context, id: string) {
    const { state, commit, rootCommit, rootState } = requestActionContext(
      context
    );
    try {
      rootCommit.START_LOADING();

      let result: AxiosResponse<RequestShowResponse> = await Axios.post(
        path + id + "/sign"
      );
      // Parse API data to include relationships
      result.data.included.forEach(incl => {
        if (
          incl.type == "user" &&
          result.data.data.relationships.user.data.id == incl.id
        ) {
          result.data.data.relationships.user.data = incl;
        } else if (
          incl.type == "batch" &&
          result.data.data.relationships.batch.data.id == incl.id
        ) {
          result.data.data.relationships.batch.data = incl;
        } else if (
          incl.type == "company" &&
          result.data.data.relationships.company.data.id == incl.id
        ) {
          result.data.data.relationships.company.data = incl;
        } else if (incl.type == "template") {
          result.data.data.relationships.templates.data.forEach(
            (template, index) => {
              if (template.id == incl.id) {
                result.data.data.relationships.templates.data[index] = incl;
              }
            }
          );
        } else if (incl.type == "form") {
          result.data.data.relationships.forms.data.forEach((form, index) => {
            if (form.id == incl.id) {
              result.data.data.relationships.forms.data[index] = incl;
            }
          });
        }
      });
      commit.SET_SELECTED(result.data.data);
      rootCommit.END_LOADING();
      return { data: result.data.data };
    } catch (error) {
      console.log(error);
      rootCommit.END_LOADING();
      return { error };
    }
  }
});
